import { BookOpen } from 'lucide-react';

import { StatusPill } from '@/components/runs/StatusPill';
import { handbookUrl } from '@/handbook/handbookUrls';
import { formatDateTime } from '@/lib/format';
import { formatDurationMs } from '@/lib/runTelemetry';
import { cn } from '@/lib/utils';
import type { RunVerdict } from '@/lib/runVerdict';

const DIAGNOSING_RUNS_URL = handbookUrl('/handbook/operations/diagnosing-runs');

export function RunVerdictCard({ verdict }: { verdict: RunVerdict }) {
  return (
    <section className="space-y-4 rounded-xl border border-border px-5 py-4" aria-label="Run summary">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <StatusPill tone={verdict.status.tone} className="text-xs">
          {verdict.status.label}
        </StatusPill>
        {verdict.model ? <span className="font-mono">{verdict.model}</span> : null}
        {verdict.startedAt ? <span>Started {formatDateTime(verdict.startedAt)}</span> : null}
      </div>

      <p className="text-base leading-7 text-foreground">{verdict.sentence}</p>

      {verdict.buckets.length > 0 ? (
        <div className="space-y-2">
          <div
            className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
            role="img"
            aria-label={verdict.buckets.map((bucket) => `${bucket.label} ${formatDurationMs(bucket.durationMs)}`).join(', ')}
          >
            {verdict.buckets.map((bucket) => (
              <div
                key={bucket.key}
                className={cn('h-full border-r border-background/60 last:border-r-0', bucket.swatch)}
                style={{ width: `${Math.max(bucket.share * 100, 0.6)}%` }}
                title={`${bucket.label}: ${formatDurationMs(bucket.durationMs)}`}
              />
            ))}
          </div>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {verdict.buckets.map((bucket) => (
              <li key={bucket.key} className="flex items-center gap-1.5">
                <span className={cn('h-2.5 w-2.5 rounded-sm', bucket.swatch)} aria-hidden="true" />
                <span className="text-foreground">{bucket.label}</span>
                <span className="tabular-nums">{formatDurationMs(bucket.durationMs)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {verdict.error ? (
        <div className="rounded-lg border border-destructive-border bg-destructive-bg/60 px-4 py-3">
          <p className="text-xs font-medium text-destructive-foreground">First error</p>
          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-5 text-destructive-foreground">
            {verdict.error}
          </pre>
        </div>
      ) : null}

      {verdict.failed ? (
        <a
          href={DIAGNOSING_RUNS_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <BookOpen className="h-4 w-4" aria-hidden="true" />
          How to diagnose a failed run
        </a>
      ) : null}
    </section>
  );
}
